import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { FcGoogle } from "react-icons/fc";
import { MdCheckCircle } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header, Container, Loader } from "../../components";
import { isAdmin } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import {
  client,
  log,
  connectGoogle,
  getGoogleConnectionStatus,
  disconnectGoogle,
  syncGmail,
  syncCalendar,
} from "../../utils";

const Perfil = () => {
  const { nombre, apellido, correo, id, pais, rol } = useAuthContext();
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [extraData, setExtraData] = useState({ cargo: "", telefono: "", cumpleanos: "" });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const canEdit = isAdmin(id);

  // Cargar datos extra del usuario (cargo, telefono, cumpleanos)
  useEffect(() => {
    const loadExtra = async () => {
      if (!id) return;
      try {
        const { data } = await client.from("usuarios").select("cargo, telefono, cumpleanos").eq("id", id).single();
        if (data) {
          setExtraData({
            cargo: data.cargo || "",
            telefono: data.telefono || "",
            cumpleanos: data.cumpleanos || "",
          });
        }
      } catch {
        // Columnas pueden no existir aún
      }
    };
    loadExtra();
  }, [id]);

  // Verificar estado de conexión de Google
  useEffect(() => {
    const checkGoogleStatus = async () => {
      if (!id) return;
      try {
        const status = await getGoogleConnectionStatus(id);
        setGoogleStatus({ ...status, loading: false });
      } catch {
        setGoogleStatus({ connected: false, loading: false });
      }
    };
    checkGoogleStatus();
  }, [id]);

  // Manejar callback de Google OAuth
  useEffect(() => {
    if (searchParams.get("google_connected") === "true") {
      toast.success("Google conectado exitosamente");
      setGoogleStatus((prev) => ({ ...prev, connected: true }));
      navigate("/admin/perfil", { replace: true });
    }
    if (searchParams.get("google_error")) {
      toast.error(searchParams.get("google_error"));
      navigate("/admin/perfil", { replace: true });
    }
  }, [searchParams, navigate]);

  const handleConnectGoogle = useCallback(async () => {
    if (!id) {
      toast.error("Error: ID de usuario no disponible. Recarga la página.");
      return;
    }
    try {
      const paisCode = getPaisCode(pais);
      await connectGoogle(id, window.location.href, paisCode);
    } catch {
      toast.error("Error al conectar con Google");
    }
  }, [id, pais]);

  const handleDisconnectGoogle = useCallback(async () => {
    try {
      await disconnectGoogle(id);
      setGoogleStatus({ connected: false, loading: false });
      toast.success("Google desconectado");
    } catch {
      toast.error("Error al desconectar Google");
    }
  }, [id]);

  const getPaisCode = (paisNombre) => {
    if (paisNombre === "Chile") return "CHI";
    if (paisNombre === "Perú") return "PER";
    if (paisNombre === "Uruguay") return "URU";
    return "CHI";
  };

  const handleSyncGoogle = useCallback(async () => {
    if (!id || syncing) return;
    setSyncing(true);
    try {
      const paisCode = getPaisCode(pais);
      const [gmailResult, calendarResult] = await Promise.all([
        syncGmail(id, paisCode, 100),
        syncCalendar(id, paisCode).catch(() => ({ synced: 0 })),
      ]);

      const totalSynced = (gmailResult?.synced || 0) + (calendarResult?.synced || 0);
      setLastSync({ date: new Date(), count: totalSynced });

      if (totalSynced > 0) {
        toast.success(`Sincronizado: ${gmailResult?.synced || 0} correos, ${calendarResult?.synced || 0} eventos`);
      } else {
        toast.success("Todo sincronizado, no hay nuevas actividades");
      }
    } catch (error) {
      log.error("handleSyncGoogle", error);
      toast.error("Error al sincronizar: " + error.message);
    } finally {
      setSyncing(false);
    }
  }, [id, pais, syncing]);

  if (correo === undefined || !id) return <Loader fullPage />;

  return (
    <div style={{ padding: "15px" }}>
      <Header leftContent={<p style={{ fontSize: "1rem", margin: 0 }}>Mi Perfil</p>} />

      <Container>
        {/* Datos personales */}
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Nombre</label>
            <input className="form-control" value={nombre || ""} disabled={!canEdit} readOnly={!canEdit} />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Apellido</label>
            <input className="form-control" value={apellido || ""} disabled={!canEdit} readOnly={!canEdit} />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Correo</label>
            <input className="form-control" value={correo || ""} disabled readOnly />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Cargo</label>
            <input
              className="form-control"
              value={extraData.cargo}
              disabled={!canEdit}
              readOnly={!canEdit}
              placeholder={canEdit ? "Ej.: Director Comercial" : "—"}
            />
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">País</label>
            <input className="form-control" value={pais || ""} disabled readOnly />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Rol</label>
            <input className="form-control" value={(rol || "").toUpperCase()} disabled readOnly />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Teléfono</label>
            <input
              className="form-control"
              value={extraData.telefono}
              disabled={!canEdit}
              readOnly={!canEdit}
              placeholder={canEdit ? "Ej.: +56 9 1234 5678" : "—"}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted small mb-1">Cumpleaños</label>
            <input
              className="form-control"
              type={canEdit ? "date" : "text"}
              value={extraData.cumpleanos}
              disabled={!canEdit}
              readOnly={!canEdit}
              placeholder="—"
            />
          </div>
        </div>

        {/* Sección de integraciones */}
        <div className="mt-5 pt-4 border-top">
          <h5 className="mb-3">Integraciones CRM</h5>
          <div className="card" style={{ width: "100%" }}>
            <div
              className="card-body"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <FcGoogle size={28} />
                <div>
                  <strong>Google</strong>
                  <div className="text-muted small">Gmail y Calendar</div>
                </div>
              </div>
              {googleStatus.loading ? (
                <span className="text-muted">Cargando...</span>
              ) : googleStatus.connected ? (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <MdCheckCircle color="#28a745" size={20} />
                  <span className="text-success small">Conectado</span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleSyncGoogle}
                    type="button"
                    disabled={syncing}
                  >
                    {syncing ? "Sincronizando..." : "Sincronizar"}
                  </button>
                  <button className="btn btn-sm btn-outline-danger" onClick={handleDisconnectGoogle} type="button">
                    Desconectar
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleConnectGoogle}
                  type="button"
                  disabled={!id}
                >
                  Conectar
                </button>
              )}
            </div>
          </div>
          <p className="text-muted small mt-2">
            Conecta tu cuenta de Google para sincronizar automáticamente correos y reuniones con el CRM.
          </p>
          {lastSync && (
            <p className="text-muted small">
              Última sincronización: {lastSync.date.toLocaleTimeString()} - {lastSync.count} actividades nuevas
            </p>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Perfil;
